import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { PaymentsAccountRow } from '../../api/client';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { Icon, Seg, Toggle } from '../ChatAssistant/compact/kit';
import {
  PLAN_ICON, PLAN_ORDER, accountName, currentPlan, dmy, int, isTurn, planCodeOf, planPriceCents, rate3, renewalPlan, usd, usdCents,
  type PlanKey,
} from './model';
import { Avatar, Check, Dash, GhostBtn, Head, PrimaryBtn, THeadRow, Table, type ConfirmSpec } from './ui';
import { ROW_LINE } from './styles';
import type { PaymentsData } from './usePaymentsData';
import { CheckoutVolumeLine } from './VolumeDiscount';
import { cartVolume, volumeOffCents } from './volume';
import { LoadingBlock } from '../Spinner';

/* Plan switch, status, cap and auto-pay columns are sized to their content; ru/uk labels
   are longer than English, so those languages get wider fixed columns. */
const LAYOUT = {
  en: { cols: '20px minmax(190px,1.3fr) 300px 120px minmax(150px,1fr) 136px 52px', min: 1140 },
  wide: { cols: '20px minmax(150px,1.3fr) 356px 150px minmax(150px,1fr) 192px 64px', min: 1180 },
};
type StatusFilter = 'all' | 'active' | 'inactive' | 'unpriced';

interface Props {
  data: PaymentsData;
  wallet: number;
  onTopUp: (amount?: number) => void;
  onConditions: (plan: PlanKey) => void;
  confirm: (spec: ConfirmSpec) => void;
  notice: (title: string, body: string) => void;
  setError: (msg: string) => void;
  /** Starting search text, e.g. one account's id from a deep link. */
  initialQuery?: string;
}

export function AccountsTab({ data, wallet, onTopUp, onConditions, confirm, notice, setError, initialQuery = '' }: Props) {
  const { t, i18n } = useTranslation('paymentsCompact');
  const { cols: COLS, min: MIN_W } = i18n.language.startsWith('en') ? LAYOUT.en : LAYOUT.wide;
  const { summary, meteredBy, metered, turn30 } = data;
  const accounts = useMemo(() => summary?.accounts ?? [], [summary]);

  /** Plan picked in the switch before the server confirms it (checkout plan for unpaid
   *  accounts, renewal plan for active ones). Cleared once the summary reloads. */
  const [choice, setChoice] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<Record<string, boolean>>({});
  const [pf, setPf] = useState<PlanKey | 'all'>('all');
  const [sf, setSf] = useState<StatusFilter>('all');
  const [q, setQ] = useState(initialQuery);
  const [sel, setSel] = useState<string[]>([]);
  const [paying, setPaying] = useState(false);

  const ladder = turn30?.rate_ladder ?? [];
  // Included is priced off the list rate — the Volume discount lowers the bill, never the block.
  const listRate = metered?.base_rate_usd ?? 0;
  const vd = summary?.volume_discount;
  const planOf = (a: PaymentsAccountRow) => currentPlan(a, choice[a.account_id]);
  const priceOf = (a: PaymentsAccountRow) => {
    const p = planOf(a);
    return p === 'assist' || p === 'none' ? planPriceCents(a, p) : 0;
  };
  const selectable = (a: PaymentsAccountRow) => {
    const p = planOf(a);
    return !a.subscription_active && (p === 'assist' || p === 'none') && priceOf(a) > 0;
  };

  const setBusyFor = (id: string, v: boolean) => setBusy((b) => ({ ...b, [id]: v }));
  const fail = (e: unknown, fallback: string) => setError(e instanceof Error && e.message ? e.message : t(fallback));

  /* ── Actions ─────────────────────────────────────────────────── */
  const savePlan = async (a: PaymentsAccountRow, plan: 'assist' | 'none') => {
    const id = a.account_id;
    const prev = choice[id];
    setChoice((c) => ({ ...c, [id]: planCodeOf(plan) }));
    setBusyFor(id, true);
    try {
      await api.updateSubscriptionAutoRenew({ account_id: id, enabled: Boolean(a.auto_renew_enabled), plan_code: planCodeOf(plan) });
      await data.reloadSummary();
    } catch (e) {
      setChoice((c) => ({ ...c, [id]: prev ?? '' }));
      fail(e, 'errors.plan');
    } finally {
      setBusyFor(id, false);
    }
    if (plan === 'assist' && planPriceCents(a, 'assist') <= 0) setSel((s) => s.filter((x) => x !== id));
  };

  const setMode = async (a: PaymentsAccountRow, mode: 'metered' | 'turn', then?: 'assist' | 'none') => {
    const id = a.account_id;
    setBusyFor(id, true);
    try {
      await api.setBillingMode({ account_id: id, mode });
      if (then) await api.updateSubscriptionAutoRenew({ account_id: id, enabled: false, plan_code: planCodeOf(then) });
      setSel((s) => s.filter((x) => x !== id));
      await Promise.all([data.reloadSummary(), data.reloadTurn(), data.reloadMetered()]);
    } catch (e) {
      fail(e, 'errors.plan');
    } finally {
      setBusyFor(id, false);
    }
  };

  const upgrade = async (a: PaymentsAccountRow) => {
    setBusyFor(a.account_id, true);
    try {
      await api.upgradeSubscriptionToAssistant({ account_id: a.account_id, idempotency_key: `upgrade:${a.account_id}:${Date.now()}` });
      await Promise.all([data.reloadSummary(), data.reloadMetered()]);
    } catch (e) {
      fail(e, 'errors.upgrade');
    } finally {
      setBusyFor(a.account_id, false);
    }
  };

  const onPlan = (a: PaymentsAccountRow, target: PlanKey) => {
    const cur = planOf(a);
    const name = accountName(a);
    if (busy[a.account_id] || cur === 'unlimited') return;
    if (cur === 'od') {
      if (target === 'od') return;
      confirm({
        title: t('confirm.fromOdTitle'),
        body: t('confirm.fromOdBody', { name, plan: t(`plan.${target}.name`) }),
        confirm: t('confirm.switch'),
        run: () => setMode(a, 'metered', target),
      });
      return;
    }
    if (target === 'od') {
      if (a.subscription_active) return;
      const r = a.turn_rate_micros ? rate3(a.turn_rate_micros / 1_000_000) : null;
      confirm({
        title: t('confirm.toOdTitle'),
        body: r ? t('confirm.toOdBody', { name, rate: r }) : t('confirm.toOdBodyNoRate', { name }),
        confirm: t('confirm.switch'),
        run: () => setMode(a, 'turn'),
      });
      return;
    }
    if (a.subscription_active) {
      if (cur === 'none' && target === 'assist') {
        confirm({
          title: t('confirm.upgradeTitle'),
          body: t('confirm.upgradeBody', { name, price: usdCents(a.assistant_price_cents, 0) }),
          confirm: t('confirm.upgrade'),
          icon: 'zap',
          run: () => upgrade(a),
        });
        return;
      }
      if (renewalPlan(a, choice[a.account_id]) !== target) void savePlan(a, target);
      return;
    }
    if (cur !== target) void savePlan(a, target);
  };

  const toggleAutoPay = async (a: PaymentsAccountRow, enabled: boolean) => {
    const plan = a.subscription_active ? renewalPlan(a, choice[a.account_id]) : (planOf(a) as 'assist' | 'none');
    setBusyFor(a.account_id, true);
    try {
      await api.updateSubscriptionAutoRenew({ account_id: a.account_id, enabled, plan_code: planCodeOf(plan) });
      await data.reloadSummary();
    } catch (e) {
      fail(e, 'errors.autoPay');
    } finally {
      setBusyFor(a.account_id, false);
    }
  };

  const setCap = (a: PaymentsAccountRow, mode: 'continue' | 'hard_stop') => {
    const cur = meteredBy[a.account_id]?.overage_mode ?? 'continue';
    if (cur === mode) return;
    const run = async () => {
      const flip = (m: 'continue' | 'hard_stop') => data.setMetered((prev) => prev && { ...prev, accounts: prev.accounts.map((x) => (x.account_id === a.account_id ? { ...x, overage_mode: m } : x)) });
      flip(mode);
      try {
        await api.setOverageCap({ account_id: a.account_id, overage_mode: mode });
        await data.reloadMetered();
      } catch (e) {
        flip(cur);
        fail(e, 'errors.cap');
      }
    };
    if (mode === 'continue') {
      confirm({ title: t('confirm.capOffTitle'), body: t('confirm.capOffBody', { name: accountName(a) }), confirm: t('confirm.capOff'), run });
    } else {
      void run();
    }
  };

  const getPrice = async (a: PaymentsAccountRow) => {
    setBusyFor(a.account_id, true);
    try {
      const res = await api.refreshAccountPrice(a.account_id);
      data.setSummary((s) => s && { ...s, accounts: s.accounts.map((x) => (x.account_id === a.account_id ? { ...x, assistant_price_cents: res.assistant_price_cents } : x)) });
      if (!res.priced) notice(t('price.unpricedTitle'), t('price.unpricedBody'));
    } catch (e) {
      const raw = e instanceof Error ? e.message : '';
      notice(t('price.failedTitle'), raw === 'REQUEST_TIMEOUT' ? t('price.timeout') : raw || t('price.failed'));
    } finally {
      setBusyFor(a.account_id, false);
    }
  };

  const pay = async () => {
    if (paying) return;
    setPaying(true);
    try {
      await api.checkoutSubscriptionsV2({
        items: selA.map((a) => ({ account_id: a.account_id, plan_code: planCodeOf(planOf(a) as 'assist' | 'none') })),
        idempotency_key: `ui-v3:${Date.now()}`,
      });
      setSel([]);
      await Promise.all([data.reloadSummary(), data.reloadMetered()]);
    } catch (e) {
      fail(e, 'errors.pay');
    } finally {
      setPaying(false);
    }
  };

  /* ── Plan cards ──────────────────────────────────────────────── */
  const prices = ladder.map((l) => l.plan_price_usd);
  const rates = ladder.map((l) => l.rate_usd);
  const noAssistCents = accounts.find((a) => a.no_assistant_price_cents > 0)?.no_assistant_price_cents ?? 0;
  const planPrice: Record<PlanKey, string> = {
    assist: prices.length ? t('plan.perMonth', { price: rangeOf(prices, (v) => usd(v, 0)) }) : t('plan.assist.priceByEarnings'),
    none: noAssistCents ? t('plan.perMonth', { price: usdCents(noAssistCents, 0) }) : '—',
    od: rates.length ? t('plan.perResponse', { price: rangeOf(rates, rate3) }) : t('plan.od.priceByEarnings'),
  };
  const counts: Record<PlanKey, number> = { assist: 0, none: 0, od: 0 };
  accounts.forEach((a) => { const p = planOf(a); if (p !== 'unlimited') counts[p] += 1; });
  // Every plan lists the CRM first, then the tools, then the AI assistant.
  const LINES: Record<PlanKey, [string, boolean][]> = {
    assist: [['plan.lines.crm', true], ['plan.lines.messages', true], ['plan.lines.aiIncluded', true]],
    none: [['plan.lines.crm', true], ['plan.lines.messages', true], ['plan.lines.noAi', false]],
    od: [['plan.lines.crm', true], ['plan.lines.messages', true], ['plan.lines.aiPerResponse', true]],
  };

  /* ── Filtering ───────────────────────────────────────────────── */
  const query = q.trim().toLowerCase();
  const shown = accounts.filter((a) => {
    const p = planOf(a);
    if (pf !== 'all' && p !== pf) return false;
    if (sf === 'active' && !(a.subscription_active || p === 'od' || p === 'unlimited')) return false;
    if (sf === 'inactive' && (a.subscription_active || p === 'od' || p === 'unlimited')) return false;
    if (sf === 'unpriced' && !(!a.subscription_active && (p === 'assist' || p === 'none') && priceOf(a) <= 0)) return false;
    if (!query) return true;
    return [a.profile_name, a.username, a.account_id].some((v) => (v || '').toLowerCase().includes(query));
  });

  const selA = accounts.filter((a) => sel.includes(a.account_id) && selectable(a));
  const subtotalCents = selA.reduce((s, a) => s + priceOf(a), 0);
  const totalCents = subtotalCents - (vd ? volumeOffCents(subtotalCents, cartVolume(vd, selA.length).bps) : 0);
  const total = totalCents / 100;
  const short = total > wallet;
  const shortBy = Math.ceil(total - wallet);

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Head title={t('plans.title')} hint={t('plans.hint')} />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 8 }}>
          {PLAN_ORDER.map((k) => {
            const on = pf === k;
            return (
              <div
                key={k}
                role="button"
                tabIndex={0}
                onClick={() => setPf(on ? 'all' : k)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPf(on ? 'all' : k); } }}
                className="pay-card"
                style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '12px 14px', background: on ? 'color-mix(in srgb, var(--om-accent) 12%, transparent)' : C.card, border: `1px solid ${on ? 'var(--om-accent-deep)' : C.border}`, borderRadius: 8, cursor: 'pointer' }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ width: 28, height: 28, flex: 'none', borderRadius: 7, background: C.tint16, color: C.purpleLt, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Icon name={PLAN_ICON[k]} size={14} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <span style={{ font: f(600, 14), color: C.text }}>{t(`plan.${k}.name`)}</span>
                    <span style={{ font: f(500, 11.5), color: C.purpleLt, whiteSpace: 'nowrap' }}>{planPrice[k]}</span>
                  </span>
                  <span style={{ font: f(500, 11.5), color: C.sub, whiteSpace: 'nowrap' }}>{t('plans.count', { count: counts[k] })}</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {LINES[k].map(([key, yes]) => (
                    <span key={key} style={{ display: 'flex', alignItems: 'center', gap: 7, font: f(400, 12.5), color: yes ? 'var(--om-body)' : C.muted }}>
                      <Icon name={yes ? 'check' : 'x'} size={13} color={yes ? C.purpleLt : C.faint} />
                      {t(key)}
                    </span>
                  ))}
                </div>
                <span style={{ font: f(400, 11.5, 1.4), color: C.muted }}>{t(`plan.${k}.foot`)}</span>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onConditions(k); }}
                  className="pay-link"
                  style={{ alignSelf: 'flex-start', height: 26, marginLeft: -8, padding: '0 8px', display: 'flex', alignItems: 'center', gap: 5, border: 0, borderRadius: 6, background: 'transparent', color: C.purpleLt, font: f(500, 12), cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  <Icon name="file-text" size={12} />
                  {t('plans.fullConditions')}
                  <Icon name="chevron-right" size={12} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Head title={t('accounts.title')} hint={shown.length === accounts.length ? t('accounts.count', { count: accounts.length }) : t('accounts.countOf', { shown: shown.length, total: accounts.length })}>
          {pf !== 'all' && (
            <button type="button" onClick={() => setPf('all')} style={{ height: 22, padding: '0 6px 0 8px', display: 'flex', alignItems: 'center', gap: 4, border: 0, borderRadius: 6, background: C.tint16, color: C.purplePale, font: f(500, 11.5), cursor: 'pointer', whiteSpace: 'nowrap' }}>
              {t(`plan.${pf}.name`)}
              <Icon name="x" size={11} />
            </button>
          )}
          <span style={{ flex: 1 }} />
          <Seg<StatusFilter>
            value={sf}
            onChange={setSf}
            options={[
              { id: 'all', label: t('accounts.filter.all') },
              { id: 'active', label: t('accounts.filter.active') },
              { id: 'inactive', label: t('accounts.filter.inactive') },
              { id: 'unpriced', label: t('accounts.filter.unpriced'), title: t('accounts.filter.unpricedTip') },
            ]}
          />
          <div className="pay-search" style={{ position: 'relative', width: 200 }}>
            <Icon name="search" size={13} color={C.muted} style={{ position: 'absolute', left: 9, top: 8 }} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t('accounts.search')}
              style={{ width: '100%', height: 30, boxSizing: 'border-box', padding: '0 9px 0 28px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: f(400, 12.5), outline: 'none' }}
            />
          </div>
          <GhostBtn icon="check-square" onClick={() => setSel(shown.filter(selectable).map((a) => a.account_id))}>{t('accounts.selectInactive')}</GhostBtn>
        </Head>

        <Table minWidth={MIN_W}>
          <THeadRow cols={COLS}>
            <span />
            <span>{t('accounts.col.account')}</span>
            <span title={t('accounts.col.planTip')}>{t('accounts.col.plan')}</span>
            <span>{t('accounts.col.status')}</span>
            <span>{t('accounts.col.use')}</span>
            <span title={t('accounts.col.capTip')}>{t('accounts.col.cap')}</span>
            <span title={t('accounts.col.autoPayTip')}>{t('accounts.col.autoPay')}</span>
          </THeadRow>
          {data.loading && !summary ? (
            <LoadingBlock pad="24px 0" size={20} label={t('loading')} />
          ) : shown.length === 0 ? (
            <div style={{ padding: '24px 14px', font: f(400, 12.5), color: C.muted }}>{t('accounts.empty')}</div>
          ) : (
            shown.map((a, i) => (
              <AccountRow
                key={a.account_id}
                a={a}
                first={i === 0}
                plan={planOf(a)}
                renewal={a.subscription_active ? renewalPlan(a, choice[a.account_id]) : null}
                priceCents={priceOf(a)}
                selected={sel.includes(a.account_id)}
                canSelect={selectable(a)}
                busy={!!busy[a.account_id]}
                listRate={listRate}
                cols={COLS}
                data={data}
                onSelect={() => setSel((s) => (s.includes(a.account_id) ? s.filter((x) => x !== a.account_id) : [...s, a.account_id]))}
                onPlan={(p) => onPlan(a, p)}
                onAutoPay={(v) => void toggleAutoPay(a, v)}
                onCap={(m) => setCap(a, m)}
                onGetPrice={() => void getPrice(a)}
              />
            ))
          )}
        </Table>
      </div>

      {selA.length > 0 && (
        <div style={{ position: 'sticky', bottom: 16, zIndex: 4, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 12px', background: C.card, border: `1px solid ${C.borderHi}`, borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,.5)' }}>
          <span style={{ font: f(600, 13), color: C.text, whiteSpace: 'nowrap' }}>{t('pay.selected', { count: selA.length })}</span>
          <button type="button" onClick={() => setSel([])} className="cpt-hover-text" style={{ height: 24, padding: '0 8px', border: 0, borderRadius: 5, background: 'transparent', color: C.sub, font: f(500, 12), cursor: 'pointer' }}>{t('pay.clear')}</button>
          {vd && <CheckoutVolumeLine vd={vd} cartCount={selA.length} subtotalCents={subtotalCents} />}
          <span style={{ flex: 1 }} />
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
            <span style={{ font: f(600, 10), letterSpacing: '.06em', textTransform: 'uppercase', color: C.muted }}>{t('pay.total')}</span>
            <span style={{ font: f(700, 16), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{usd(total)}</span>
          </div>
          {short && (
            <button type="button" onClick={() => onTopUp(shortBy)} style={{ height: 32, padding: '0 12px', display: 'flex', alignItems: 'center', gap: 6, border: '1px solid var(--om-accent-deep)', borderRadius: 7, background: C.tint16, color: C.purplePale, font: f(600, 12.5), cursor: 'pointer', whiteSpace: 'nowrap' }}>
              <Icon name="plus" size={12} />
              {t('pay.topUpShort', { amount: usd(shortBy, 0) })}
            </button>
          )}
          <PrimaryBtn icon="credit-card" onClick={() => void pay()} disabled={short || paying || totalCents <= 0} style={{ font: f(600, 12.5), padding: '0 14px' }}>
            {paying ? t('pay.paying') : t('pay.payFromWallet')}
          </PrimaryBtn>
        </div>
      )}
    </>
  );
}

function rangeOf(vals: number[], fmt: (v: number) => string) {
  const lo = Math.min(...vals), hi = Math.max(...vals);
  return lo === hi ? fmt(lo) : fmt(lo) + '–' + fmt(hi);
}

/* ── One account row ─────────────────────────────────────────────── */
interface RowProps {
  a: PaymentsAccountRow;
  first: boolean;
  plan: PlanKey | 'unlimited';
  renewal: PlanKey | null;
  priceCents: number;
  selected: boolean;
  canSelect: boolean;
  busy: boolean;
  listRate: number;
  cols: string;
  data: PaymentsData;
  onSelect: () => void;
  onPlan: (p: PlanKey) => void;
  onAutoPay: (v: boolean) => void;
  onCap: (m: 'continue' | 'hard_stop') => void;
  onGetPrice: () => void;
}

function AccountRow({ a, first, plan, renewal, priceCents, selected, canSelect, busy, listRate, cols, data, onSelect, onPlan, onAutoPay, onCap, onGetPrice }: RowProps) {
  const { t } = useTranslation('paymentsCompact');
  const name = accountName(a);
  const active = a.subscription_active;
  const mu = data.meteredBy[a.account_id];
  const tu = data.turnBy[a.account_id];
  const turn = isTurn(a);
  const isA = plan === 'assist', isN = plan === 'none', isO = plan === 'od';

  /* Status */
  let st: string, stSub: string, on = true;
  if (isO) {
    if (a.billing_suspended) { st = t('status.paused'); stSub = t('status.walletEmpty'); on = false; }
    else { st = t('status.payg'); stSub = t('status.walletFunded'); }
  } else if (plan === 'unlimited') { st = t('status.active'); stSub = t('status.operator'); }
  else if (active) {
    st = t('status.active');
    stSub = mu?.suspended ? t('status.capReached') : a.subscription_period_end ? t('status.until', { date: dmy(a.subscription_period_end) }) : '';
  } else { st = t('status.inactive'); stSub = priceCents > 0 ? t('status.notPaid') : t('status.noPrice'); on = false; }

  /* Price line under the plan switch */
  let priceTxt = '', priceC: string = C.muted;
  if (isO) priceTxt = a.turn_rate_micros ? t('row.perResponse', { rate: rate3(a.turn_rate_micros / 1_000_000) }) : t('row.rateUnknown');
  else if (plan === 'unlimited') priceTxt = t('row.operatorPlan');
  else if (active && renewal && renewal !== plan) { priceTxt = t('row.renewsAs', { plan: t(`plan.${renewal}.name`) }); priceC = C.purplePale; }
  else if (priceCents > 0) priceTxt = t('row.perMonth', { price: usdCents(priceCents, priceCents % 100 ? 2 : 0) });
  else { priceTxt = t('row.priceUnknown'); priceC = C.purplePale; }

  /* Assistant use · 30 days */
  let use: ReactNode;
  const metered = a.billing_mode === 'metered' && mu;
  if (isA && active && metered) {
    const used = mu.used_this_period, incl = mu.included_turns;
    const over = Math.max(0, used - incl);
    const pct = incl > 0 ? used / incl : 0;
    use = (
      <>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, font: f(500, 12), color: C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden' }}>
          {int(used)} / {int(incl)}
          <span style={{ font: f(400, 11), color: over ? C.purplePale : C.muted, overflow: 'hidden', textOverflow: 'ellipsis' }}>{over ? t('row.over', { n: int(over) }) : Math.round(pct * 100) + '%'}</span>
        </div>
        <div style={{ height: 4, borderRadius: 99, background: C.border, overflow: 'hidden' }}>
          <div style={{ height: '100%', width: Math.min(100, pct * 100) + '%', background: over ? C.purplePale : C.purple, borderRadius: 99 }} />
        </div>
      </>
    );
  } else if (isA && active) {
    use = <UseText>{t('row.flatPlan')}</UseText>;
  } else if (isA && priceCents > 0) {
    const rate = mu?.list_rate_usd || listRate;
    use = <UseText color={C.sub}>{rate > 0 ? t('row.included', { n: int(Math.floor(priceCents / 100 / rate)) }) : t('row.includedUnknown')}</UseText>;
  } else if (isA || (isN && priceCents <= 0)) {
    use = (
      <button type="button" onClick={onGetPrice} disabled={busy} title={t('row.getPriceTip')} className={`cpt-hover-border${busy ? ' om-busy' : ''}`} style={{ alignSelf: 'flex-start', height: 24, padding: '0 9px', display: 'flex', alignItems: 'center', gap: 5, background: C.tint16, border: '1px solid var(--om-accent-deep)', borderRadius: 6, color: C.purplePale, font: f(500, 11.5), cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
        <Icon name="tag" size={11} />
        <span>{t('row.getPrice')}</span>
      </button>
    );
  } else if (isO) {
    use = tu ? (
      <UseText>
        {usdCents(tu.amount_charged_cents)} <span style={{ font: f(400, 11), color: C.muted }}>{t('row.responses', { n: int(tu.responses_billed) })}</span>
      </UseText>
    ) : <Dash />;
  } else if (isN) {
    use = <UseText color={C.muted}>{t('row.noAssistant')}</UseText>;
  } else {
    use = <UseText color={C.sub}>{t('row.unlimited')}</UseText>;
  }

  const hasAutoPay = (isA || isN) && !turn;
  const autoPayBlocked = !a.auto_renew_enabled && a.assistant_price_cents <= 0;

  return (
    <div style={{ display: 'grid', gridTemplateColumns: cols, gap: 12, alignItems: 'center', minHeight: 48, padding: '6px 14px', borderTop: first ? 0 : ROW_LINE, background: selected ? 'color-mix(in srgb, var(--om-accent) 7%, transparent)' : 'transparent', opacity: busy ? 0.7 : 1, transition: 'opacity .15s' }}>
      <Check on={selected && canSelect} onClick={onSelect} disabled={!canSelect} title={canSelect ? t('row.selectTip') : active || isO ? t('row.alreadyActive') : t('row.needsPrice')} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, minWidth: 0 }}>
        <Avatar name={name} src={a.avatar_url} />
        <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <span title={name} style={{ font: f(500, 13), color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
          <span style={{ font: f(400, 11.5), color: C.muted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>@{a.username || a.account_id}</span>
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
        {plan === 'unlimited' ? (
          <span style={{ font: f(500, 11.5), color: C.purplePale, padding: '3px 9px', border: `1px solid ${C.tintLine}`, borderRadius: 99 }}>{t('row.unlimited')}</span>
        ) : (
          <Seg<PlanKey>
            size="sm"
            value={plan}
            onChange={onPlan}
            disabled={busy}
            options={PLAN_ORDER.map((k) => ({
              id: k,
              label: t(`plan.${k}.name`),
              disabled: k === 'od' && active && !isO,
              title: k === 'od' && active && !isO ? t('row.odAfterSub') : active && !isO && k !== plan ? (k === 'assist' ? t('row.upgradeTip') : t('row.nextRenewalTip')) : undefined,
            }))}
          />
        )}
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, font: f(400, 11), color: priceC, whiteSpace: 'nowrap' }}>
          {priceTxt}
          {/* Active accounts show the usage bar in the use column, so the price lookup lives here */}
          {(isA || isN) && active && priceCents <= 0 && (
            <button type="button" onClick={onGetPrice} disabled={busy} title={t('row.getPriceTip')} className={`cpt-hover-border${busy ? ' om-busy' : ''}`} style={{ height: 20, padding: '0 7px', display: 'flex', alignItems: 'center', gap: 4, background: C.tint16, border: '1px solid var(--om-accent-deep)', borderRadius: 6, color: C.purplePale, font: f(500, 11), cursor: busy ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
              <Icon name="tag" size={10} />
              <span>{t('row.getPrice')}</span>
            </button>
          )}
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, font: f(500, 12.5), color: on ? C.text : C.sub, whiteSpace: 'nowrap' }}>
          <span style={{ width: 6, height: 6, borderRadius: 99, background: on ? C.purple : C.faint }} />
          {st}
        </span>
        <span style={{ font: f(400, 11), color: C.muted, whiteSpace: 'nowrap' }}>{stSub}</span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>{use}</div>
      <div>
        {isA && a.billing_mode === 'metered' && mu ? (
          <Seg<'continue' | 'hard_stop'>
            size="sm"
            value={mu.overage_mode}
            onChange={onCap}
            options={[
              { id: 'continue', label: t('cap.off'), title: t('cap.offTip') },
              { id: 'hard_stop', label: t('cap.on'), title: t('cap.onTip') },
            ]}
          />
        ) : <Dash title={isO ? t('cap.odTip') : undefined} />}
      </div>
      <div>
        {hasAutoPay ? (
          <Toggle
            on={Boolean(a.auto_renew_enabled)}
            onChange={onAutoPay}
            disabled={busy || autoPayBlocked}
            title={autoPayBlocked ? t('row.autoPayNeedsPrice') : a.auto_renew_enabled ? t('row.autoPayOn') : t('row.autoPayOff')}
          />
        ) : <Dash title={isO ? t('row.autoPayOd') : undefined} />}
      </div>
    </div>
  );
}

function UseText({ children, color = C.text }: { children: ReactNode; color?: string }) {
  return <span style={{ font: f(500, 12), color, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{children}</span>;
}
